import {memo} from 'react'
import {ScopedCommands} from '@github-ui/ui-commands'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {capitalize} from '@github-ui/filter-core'
import {RadioGroup, FormControl, Radio} from '@primer/react'
import {Tooltip} from '@primer/react/deprecated'
import {ReviewEvent} from '../../../mutations/use-submit-review-mutation'
import type {AllowedNonCommentReviewType} from '../../../page-data/payloads/toolbar'
import styles from './ReviewMenu.module.css'

export function ReviewTypeSelector({
  isPROpen,
  reviewEvent,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
  handleReviewEventChange,
  handleReviewSubmit,
}: {
  isPROpen: boolean
  reviewEvent: ReviewEvent
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  viewerCanWriteToRepo: boolean
  viewerHasViolatedPushPolicy?: boolean | null
  viewerIsAuthor: boolean
  viewerIsCopilotAttributed: boolean
  handleReviewEventChange: (newReviewEvent: string | null) => void
  handleReviewSubmit: () => void
}) {
  if (!isPROpen) return null

  return (
    <ScopedCommands
      commands={{
        'pull-request-files-changed:submit-review': handleReviewSubmit,
      }}
    >
      <ReviewRadioButtons
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        onReviewEventChange={handleReviewEventChange}
      />
    </ScopedCommands>
  )
}

export const ReviewRadioButtons = memo(function ReviewRadioButtons({
  onReviewEventChange,
  reviewEvent,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
}: {
  onReviewEventChange: (selected: string | null) => void
  reviewEvent: ReviewEvent
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  viewerCanWriteToRepo: boolean
  viewerHasViolatedPushPolicy: boolean | null | undefined
  viewerIsAuthor: boolean
  viewerIsCopilotAttributed: boolean
}) {
  return (
    <RadioGroup name="reviewEvent" onChange={onReviewEventChange} className={styles.RadioGroup}>
      <RadioGroup.Label visuallyHidden>Review Event</RadioGroup.Label>
      <RadioButton
        checked={reviewEvent === ReviewEvent.comment}
        label={capitalize(ReviewEvent.comment)}
        subLabel="Submit general feedback without explicit approval."
        value={ReviewEvent.comment}
      />
      <RadioButton
        checked={reviewEvent === ReviewEvent.approve}
        disabled={!viewerAllowedNonCommentReviewTypes.includes('APPROVE')}
        label={capitalize(ReviewEvent.approve)}
        subLabel={getApproveSubLabel(viewerIsCopilotAttributed)}
        value={ReviewEvent.approve}
        disabledTooltip={getApproveDisabledTooltip(
          viewerIsAuthor,
          !!viewerHasViolatedPushPolicy,
          viewerAllowedNonCommentReviewTypes,
        )}
      />
      <RadioButton
        checked={reviewEvent === ReviewEvent.requestChanges}
        disabled={!viewerAllowedNonCommentReviewTypes.includes('REQUEST_CHANGES')}
        disabledTooltip={getRequestChangesDisabledTooltip(viewerIsAuthor, viewerAllowedNonCommentReviewTypes)}
        label={capitalize(ReviewEvent.requestChanges)}
        value={ReviewEvent.requestChanges}
        subLabel={
          viewerCanWriteToRepo
            ? 'Submit feedback that must be addressed before merging.'
            : 'Submit feedback suggesting changes.'
        }
      />
    </RadioGroup>
  )
})

function RadioButton({
  checked,
  disabled,
  disabledTooltip,
  label,
  subLabel,
  value,
}: {
  checked?: boolean
  disabled?: boolean
  disabledTooltip?: string
  label: string
  subLabel: string
  value: string
}) {
  const radioControl = (
    <FormControl disabled={disabled}>
      <Radio checked={checked} value={value} className={styles.Radio} />
      <FormControl.Label className="d-flex flex-column">
        <span className="text-bold">{label}</span>
        <span className={styles.RadioText}>{subLabel}</span>
      </FormControl.Label>
    </FormControl>
  )

  return disabled && disabledTooltip ? <Tooltip text={disabledTooltip}>{radioControl}</Tooltip> : radioControl
}

function getApproveDisabledTooltip(
  reviewerIsAuthor: boolean,
  violatedPushPolicy: boolean,
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[],
) {
  if (reviewerIsAuthor) return "Pull request authors can't approve their own pull requests."
  if (violatedPushPolicy) return "Users that pushed changes to this pull request after it was opened can't approve"
  if (!viewerAllowedNonCommentReviewTypes.includes('APPROVE'))
    return 'Only users with explicit access to this repository may approve pull requests'
  return ''
}

function getRequestChangesDisabledTooltip(
  reviewerIsAuthor: boolean,
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[],
) {
  if (reviewerIsAuthor) return "Pull request authors can't request changes on their own pull requests."
  if (!viewerAllowedNonCommentReviewTypes.includes('REQUEST_CHANGES'))
    return 'Only users with explicit access to this repository may request changes on pull requests'
  return ''
}

function getApproveSubLabel(viewerIsCopilotAttributed: boolean) {
  if (viewerIsCopilotAttributed) {
    return `Only users who did not collaborate with the ${isFeatureEnabled('cca_rename') ? 'cloud agent' : 'coding agent'} will satisfy review requirements.`
  }

  return 'Submit feedback and approve merging these changes.'
}
